import React, { useState, useEffect } from "react";
import {
  IonButton,
  IonIcon,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonInput,
  IonButtons,
  IonTextarea,
  IonSpinner,
  IonAlert,
  IonCheckbox,
} from "@ionic/react";
import {
  closeOutline,
  imageOutline,
  camera,
  lockClosed,
  addCircleOutline,
} from "ionicons/icons";
import { useDispatch } from "react-redux";
import { updateHashtag } from "../../api/requests/hashtag";
import { getTrends } from "../../redux/actions/postActions";
import "./CreateCommunity.css";

const EditCommunityModal = ({ isOpen, onClose, communityData, onSuccess }) => {
  const dispatch = useDispatch();

  const [communityName, setCommunityName] = useState("");
  const [communityDescription, setCommunityDescription] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [rules, setRules] = useState([]);
  useEffect(() => {
    if (communityData) {
      setCommunityName(communityData.displayName || "");
      setCommunityDescription(communityData.description || "");
      setIsPrivate(communityData.isPrivate || false);
      setRules(communityData.rules || []);
    }
  }, [communityData]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) setSelectedImage(file);
  };

  const handleUpdateCommunity = async () => {
    if (!communityName.trim()) return;

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("displayName", communityName.trim());
      formData.append("description", communityDescription.trim());
      formData.append("isPrivate", isPrivate);
      formData.append(
        "rules",
        JSON.stringify(rules.filter((rule) => rule.trim() !== "")),
      );
      if (selectedImage) formData.append("image", selectedImage);

      // The tag extracted from communityData, assuming communityData.name is like "#example"
      const tag = communityData.name.replace("#", "");

      const response = await updateHashtag(tag, formData);

      if (response.status === 200) {
        setUpdateStatus("success");
        setAlertMessage("Community updated successfully!");
        setShowAlert(true);
        dispatch(getTrends());
        // 4. Trigger the refresh in the parent component
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setUpdateStatus("error");
        setAlertMessage(response.data?.message || "Failed to update community");
        setShowAlert(true);
      }
    } catch (error) {
      setUpdateStatus("error");
      setAlertMessage("Failed to update community. Please try again.");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlertDismiss = () => {
    setShowAlert(false);
    if (updateStatus === "success") {
      onClose();
    }
    setUpdateStatus(null);
    setAlertMessage("");
  };

  const isFormValid = communityName.trim().length > 0;

  return (
    <div className="create-community-modal poppins">
      <IonModal isOpen={isOpen} onDidDismiss={() => !isLoading && onClose()}>
        <IonHeader className="">
          <IonToolbar className="modal-header-toolbar">
            <IonTitle className="modal-title poetsen">Edit Community</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onClose} disabled={isLoading}>
                <IonIcon icon={closeOutline} className="close-icon" />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding community-modal-content">
          <div className="modal-container">
            {/* Name Input */}
            <div>
              <label className="input-label">Community Name</label>
              <div className="input-wrapper">
                <IonInput
                  className="community-input"
                  value={communityName}
                  onIonInput={(e) => setCommunityName(e.detail.value)}
                  placeholder="Enter community name"
                />
              </div>
            </div>

            {/* Description Input */}
            <div>
              <label className="input-label">Description</label>
              <div className="input-wrapper">
                <IonTextarea
                  className="community-textarea"
                  value={communityDescription}
                  onIonInput={(e) => setCommunityDescription(e.detail.value)}
                  placeholder="Add a description for your community..."
                  rows={3}
                />
              </div>
            </div>
            <div>
              <label className="input-label">Community Rules</label>

              <div className="rules-container">
                {rules.map((rule, index) => (
                  <div key={index} className="rule-item">
                    <IonInput
                      className="rule-input"
                      value={rule}
                      onIonInput={(e) => {
                        const newRules = [...rules];
                        newRules[index] = e.detail.value;
                        setRules(newRules);
                      }}
                      placeholder={`Rule ${index + 1}`}
                    />

                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={() => {
                        const newRules = rules.filter((_, i) => i !== index);
                        setRules(newRules);
                      }}
                      disabled={rules.length <= 1}
                    >
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </div>
                ))}

                {rules.length < 10 && (
                  <IonButton
                    className="add-rule-btn"
                    fill="clear"
                    onClick={() => setRules([...rules, ""])}
                  >
                    <IonIcon icon={addCircleOutline} /> Add Rule
                  </IonButton>
                )}
              </div>
            </div>
            {/* Logo Upload */}
            <div>
              <input
                type="file"
                id="editCommImg"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              <label htmlFor="editCommImg" className="logo-upload-box">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div className="upload-icon-container">
                    <IonIcon icon={imageOutline} style={{ color: "#d80621" }} />
                  </div>
                  <span
                    className="poetsen"
                    style={{ fontWeight: "400", color: "#000" }}
                  >
                    Upload logo
                  </span>
                </div>
                <div className="camera-badge">
                  <IonIcon icon={camera} style={{ color: "#fff" }} />
                </div>
              </label>
              <span style={{ fontSize: "12px", color: "#6B7280" }}>
                {selectedImage ? selectedImage.name : ""}
              </span>
            </div>

            {/* Privacy */}
            <div
              style={{
                paddingBottom: "10px",
                borderBottom: "1px solid #F2F2F2",
              }}
            >
              <label
                className="input-label"
                style={{ marginTop: "20px", marginBottom: "20px" }}
              >
                Privacy
              </label>
              <div className="privacy-item-container">
                <div className="privacy-info">
                  <div className="lock-icon-bg">
                    <IonIcon icon={lockClosed} style={{ color: "#fff" }} />
                  </div>
                  <div>
                    <div
                      style={{ fontWeight: "400", color: "#000" }}
                      className="poetsen"
                    >
                      Private
                    </div>
                    <div
                      style={{
                        color: "#9ca3af",
                        fontSize: "14px",
                      }}
                    >
                      A private community
                    </div>
                  </div>
                </div>
                <IonCheckbox
                  className="privacy-checkbox"
                  checked={isPrivate}
                  onIonChange={(e) => setIsPrivate(e.detail.checked)}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="button-group">
              <IonButton
                className="submit-button"
                expand="block"
                onClick={handleUpdateCommunity}
                disabled={!isFormValid || isLoading}
                style={{
                  "--background": isFormValid ? "#d80621" : "#f1a1ab",
                }}
              >
                {isLoading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  "Update Community"
                )}
              </IonButton>

              <IonButton
                className="cancel-button"
                expand="block"
                fill="clear"
                onClick={onClose}
              >
                Cancel
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={showAlert}
        onDidDismiss={handleAlertDismiss}
        header="Community"
        message={alertMessage}
        buttons={["OK"]}
      />
    </div>
  );
};

export default EditCommunityModal;
